import { useMemo, useState, useEffect } from 'react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import {
    Plus,
    Settings,
    Check,
    BookOpen,
    FileText
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useManuscripts, DEFAULT_MANUSCRIPT_ID } from '@/context/manuscripts-context';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { usePassage } from '@/lib/hooks/use-passage';
import { ManuscriptEditDialog } from './manuscript-edit-dialog';
import notebookPenIcon from '@/assets/icons/notebook-pen.png';
import notebookPenIconDark from '@/assets/icons/notebook-pen-dark.png';

// Custom icon using the exact notebook-pen image
const CustomNotebookPen = ({ className }: { className?: string }) => {
    const isDark = useDarkMode();

    return (
        <img
            src={isDark ? notebookPenIconDark : notebookPenIcon}
            alt="Notebook with pen"
            className={className}
        />
    );
};

const CustomNotebookPenOff = ({ className }: { className?: string }) => (
    <div className={`relative flex items-center justify-center ${className}`}>
        <CustomNotebookPen className="h-full w-full" />
        <div className="absolute inset-0 flex items-center justify-center rotate-45">
            <div className="w-[140%] h-[1.5px] bg-foreground/70 rounded-full" />
        </div>
    </div>
);

export function ManuscriptSelectorDropdown() {
    const {
        activeManuscriptId,
        setActiveManuscriptId,
        activeManuscript,
        defaultManuscript,
        manuscripts,
        getManuscriptsForPassage,
        loading
    } = useManuscripts();

    const { passageId, scrollState, version, showAnnotations, setShowAnnotations } = usePassageState();
    const { section: currentPassage } = usePassage();
    const [menuOpen, setMenuOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingManuscriptId, setEditingManuscriptId] = useState<string | null>(null);

    const visibleManuscripts = useMemo(() => {
        // Get current book from passage data or scroll state
        const currentBook = currentPassage?.book || scrollState.book || '';
        const list = getManuscriptsForPassage(passageId || '', currentBook, version);



        if (list.length === 0 && defaultManuscript) {
            return [defaultManuscript];
        }
        return list;
    }, [getManuscriptsForPassage, passageId, currentPassage?.book, scrollState.book, version, manuscripts, defaultManuscript]);

    // Auto-switch to available manuscript when navigating to different context
    useEffect(() => {
        if (!activeManuscriptId || !showAnnotations) return;

        // Check if current active manuscript is still visible in this context
        const isActiveVisible = visibleManuscripts.some(m => m.id === activeManuscriptId);

        if (!isActiveVisible && visibleManuscripts.length > 0) {
            // Switch to most recently updated manuscript
            const sortedByRecent = [...visibleManuscripts].sort((a, b) =>
                (b.dateUpdated || 0) - (a.dateUpdated || 0)
            );
            setActiveManuscriptId(sortedByRecent[0].id);
        }
    }, [activeManuscriptId, showAnnotations, visibleManuscripts, setActiveManuscriptId]);

    const handleSelect = (id: string | null) => {
        if (id === null) {
            setShowAnnotations(false);
            setActiveManuscriptId(null);
        } else {
            setActiveManuscriptId(id);
            setShowAnnotations(true);
        }
    };

    const handleEdit = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        e.preventDefault();
        setMenuOpen(false);
        setEditingManuscriptId(id);
        setEditDialogOpen(true);
    };

    const handleCreate = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setMenuOpen(false);
        setEditingManuscriptId(null);
        setEditDialogOpen(true);
    };

    const currentName = activeManuscriptId && showAnnotations
        ? activeManuscript?.name || 'Manuscript'
        : 'Hide All Marks';

    // Handle both real manuscripts and the ghost default
    const displayManuscript = activeManuscript || (activeManuscriptId === DEFAULT_MANUSCRIPT_ID ? defaultManuscript : null);

    // Use custom notebook pen icon
    const currentIcon = activeManuscriptId && showAnnotations && displayManuscript
        ? <CustomNotebookPen className="h-4 w-4" />
        : <CustomNotebookPenOff className="h-4 w-4 opacity-40" />;

    // Only show manuscript selector in dig mode (when viewing a passage)
    if (!passageId) {
        return null;
    }

    return (
        <>
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-12 w-12 p-0" title={currentName}>
                        <div className="relative flex items-center justify-center">
                            {currentIcon}
                            {activeManuscriptId && showAnnotations && visibleManuscripts.length > 1 && (
                                <div
                                    className="absolute -top-1 -right-1 h-2 w-2 rounded-full border-2 border-background"
                                    style={{ backgroundColor: activeManuscript?.color }}
                                />
                            )}
                        </div>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[240px]">
                    <DropdownMenuLabel>
                        <div className="flex items-center justify-between">
                            <span>Manuscripts</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCreate}>
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                        <p className="text-[10px] font-normal text-muted-foreground leading-snug mt-1 opacity-80">
                            Personal copies for marking & study.
                        </p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    {visibleManuscripts.map((m) => (
                        <DropdownMenuItem
                            key={m.id}
                            onClick={() => handleSelect(m.id)}
                            className="flex items-center justify-between group cursor-pointer"
                        >
                            <div className="flex items-center gap-2 overflow-hidden">
                                {activeManuscriptId === m.id ? (
                                    <Check className="h-4 w-4 flex-shrink-0" />
                                ) : (
                                    <div className="w-4 flex-shrink-0" />
                                )}
                                <div className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
                                <span className="truncate">{m.name}</span>
                                {m.scope === 'book' && <BookOpen className="h-3 w-3 text-muted-foreground opacity-70" />}
                                {m.scope === 'passages' && <FileText className="h-3 w-3 text-muted-foreground opacity-70" />}
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => handleEdit(e, m.id)}
                            >
                                <Settings className="h-3 w-3" />
                            </Button>
                        </DropdownMenuItem>
                    ))}

                    {manuscripts.length > 0 && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => handleSelect(null)}
                                className="flex items-center gap-2 cursor-pointer"
                            >
                                {!activeManuscriptId || activeManuscriptId === 'null' ? (
                                    <Check className="h-4 w-4" />
                                ) : (
                                    <div className="w-4" />
                                )}
                                <CustomNotebookPen className="h-4 w-4 opacity-40" />
                                <span>Hide All Marks</span>
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <ManuscriptEditDialog
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                manuscriptId={editingManuscriptId}
            />
        </>
    );
}
